import React, { Fragment, useMemo } from 'react'
import { DotsThreeVertical, X, PlusCircle, ArrowBendDownRight } from '@phosphor-icons/react'
import { useDynamicSegment } from 'src/lib/contexts/DynamicSegmentContext'
import { DynamicSegmentFieldType, DynamicSegmentFilterType, DynamicSegmentOperatorInfoType } from 'src/lib/types/segment'
import { useLocale } from 'src/lib/hooks/useLocal'
import FilterModal from './FilterModal'
import { getDynamicSegmentIcon, getDynamicSegmentUIFilterType, mapEventData } from 'src/lib/utils/segmentsHelper'
import GenericSelectionModal from 'src/components/common/select-component/GenericSelectionModal'
import { FilterValueBuilder } from './FilterValueBuilder'
import { generateUUID } from 'src/lib/utils/helpers'
import { Button, Icon, IconButton } from '@shopflo/ui'
import {
  CONDITION_CONTAINER_PAD_HOVER_CLASS,
  CONDITION_CONTAINER_PAD_STATIC_CLASS,
  FIELD_PILL_BORDERED_CLASS as FIELD_PILL_CLASS,
  PILL_INTERACTIVE_CLASS
} from '../condition-classes'

// Field name pill — matches static list "State" badge (border-l-1 border-r-1, Icon size xxs)

/** Per-condition horizontal inset + hover (parent scroll area has no px-8). */

const READONLY_OPERATOR_PILL_CLASS =
  'flex whitespace-nowrap items-center gap-1 px-1 py-0.5 rounded-md text-text-sm-medium text-zinc-600 bg-zinc-950/5 shrink-0'

/** Full-width hairline between last Where block and first Who (no horizontal inset). */
const WHERE_WHO_DIVIDER_CLASS =
  'relative shrink-0 py-0.5 before:pointer-events-none before:absolute before:left-0 before:right-0 before:top-1/2 before:h-px before:-translate-y-1/2 before:bg-zinc-500/8 before:content-[""]'

/** Between consecutive top-level Where rows only (Where → and → and); not inside MULTI property stacks. */
const BETWEEN_TOP_LEVEL_WHERE_DIVIDER_CLASS =
  'relative shrink-0 py-0.5 before:pointer-events-none before:absolute before:left-[88px] before:right-0 before:top-1/2 before:h-px before:-translate-y-1/2 before:bg-zinc-500/6 before:content-[""]'

interface SegmentConditionFilterLayoutProps {
  readOnly?: boolean
}

const SegmentConditionFilterLayout: React.FC<SegmentConditionFilterLayoutProps> = ({ readOnly = false }) => {
  const { t } = useLocale()
  const { segmentFormState, schemaData, actions } = useDynamicSegment()
  const { deleteFilter, addNewFilter, updateFilter } = actions
  const rowPadClass = readOnly ? CONDITION_CONTAINER_PAD_STATIC_CLASS : CONDITION_CONTAINER_PAD_HOVER_CLASS

  const { whereFilters, whoFilters } = useMemo(() => {
    const where: DynamicSegmentFilterType[] = []
    const who: DynamicSegmentFilterType[] = []

    segmentFormState.filters.forEach(filter => {
      if (getDynamicSegmentUIFilterType(filter.field) === 'EVENT') {
        who.push(filter)
      } else {
        where.push(filter)
      }
    })

    return { whereFilters: where, whoFilters: who }
  }, [segmentFormState.filters])

  const preparedWhoBlocks = useMemo(() => {
    const blocks: Array<{
      filter: DynamicSegmentFilterType
      nameFilter: DynamicSegmentFilterType
      timeStampFilter: DynamicSegmentFilterType
    }> = []
    whoFilters.forEach(filter => {
      const nameFilter = filter.children.find(
        (childFilter: DynamicSegmentFilterType) =>
          childFilter.field.tableId === filter.field.tableId &&
          childFilter.field.columnReference === 'EVENT_NAME'
      )
      const timeStampFilter = filter.children.find(
        (childFilter: DynamicSegmentFilterType) =>
          childFilter.field.tableId === filter.field.tableId &&
          childFilter.field.columnReference === 'SERVER_TIMESTAMP'
      )
      if (nameFilter && timeStampFilter) {
        blocks.push({ filter, nameFilter, timeStampFilter })
      }
    })
    return blocks
  }, [whoFilters])

  return (
    <div className='flex flex-col'>
      {whereFilters.map((filter: DynamicSegmentFilterType, index: number) => {
        const whereLabel = (
          <div className='min-w-[3rem] shrink-0'>
            <div className={`h-[26px] flex items-center pr-1 ${index === 0 ? 'justify-start' : 'justify-end'}`}>
              <span className={`text-text-sm-medium ${index === 0 ? 'text-zinc-500' : 'text-zinc-500/70'}`}>{index === 0 ? t('segment_creation.where_label') : t('segment_creation.and')}</span>
            </div>
          </div>
        )

        if (getDynamicSegmentUIFilterType(filter.field) === 'MULTI') {
          const availableAdditionalFields: DynamicSegmentFieldType[] = schemaData.fields.filter((field: DynamicSegmentFieldType) => field.tableId === filter.field.tableId && !field.id.includes("SYS_COUNT"))
          const [firstChild, ...restChildren] = filter.children
          const setChildFilter = (f: DynamicSegmentFilterType) =>
            updateFilter({ ...filter, children: filter.children.map(child => child.id === f.id ? f : child) })

          return (
            <Fragment key={filter.id}>
              {index > 0 ? (
                <div className={BETWEEN_TOP_LEVEL_WHERE_DIVIDER_CLASS} aria-hidden />
              ) : null}
              <div className={`flex flex-col pb-2.5 ${rowPadClass}`}>
              {/* Main Where row — like Who: one flex-col stacks count + first property; ⋮/X align to top (items-start) */}
              <div className='flex items-start gap-3 pt-2.5 pb-2.5'>
                {whereLabel}
                <div className='flex-1 min-w-0 flex flex-col gap-1.5 overflow-visible'>
                  <FilterValueBuilder filter={filter} setFilter={updateFilter} readOnly={readOnly} />
                  {firstChild && (
                    <>
                      <div className='flex items-start gap-1 flex-wrap'>
                        <div className={FIELD_PILL_CLASS}>
                          <Icon IconComponent={getDynamicSegmentIcon(firstChild.field.category)} size='xs' className='text-gray-400' />
                          <span className='text-text-sm-medium text-zinc-600'>{firstChild.field.name}</span>
                        </div>
                      </div>
                      <div className='flex items-start gap-1 flex-wrap min-w-0'>
                        <FilterValueBuilder filter={firstChild} setFilter={setChildFilter} readOnly={readOnly} />
                      </div>
                    </>
                  )}
                </div>
                {!readOnly ? (
                  <div className='flex items-center shrink-0'>
                    <IconButton IconComponent={DotsThreeVertical} size='xs' variant='ghost' />
                    <IconButton IconComponent={X} size='xs' variant='ghost' onClick={() => deleteFilter(filter.id)} />
                  </div>
                ) : null}
              </div>

              {/* Further properties — same as event additional rows (↳ and in label column) */}
              {restChildren.map((childFilter: DynamicSegmentFilterType) => (
                <div key={childFilter.id} className='flex items-start gap-3'>
                  <div className='min-w-[3rem] shrink-0'>
                    <div className='h-[26px] flex items-center justify-end pr-1 gap-1'>
                      <Icon IconComponent={ArrowBendDownRight} size='xs' className='text-zinc-500/40' />
                      <span className='text-text-sm-medium text-zinc-500/70'>{t('segment_creation.and')}</span>
                    </div>
                  </div>
                  <div className='flex-1 min-w-0 flex flex-col gap-1.5 overflow-visible'>
                    <div className='flex items-start gap-1 flex-wrap'>
                      <div className={FIELD_PILL_CLASS}>
                        <Icon IconComponent={getDynamicSegmentIcon(childFilter.field.category)} size='xs' className='text-gray-400' />
                        <span className='text-text-sm-medium text-coal-dark'>{childFilter.field.name}</span>
                      </div>
                    </div>
                    <div className='flex items-start gap-1 flex-wrap pb-2'>
                      <FilterValueBuilder filter={childFilter} setFilter={setChildFilter} readOnly={readOnly} />
                    </div>
                  </div>
                  {!readOnly ? (
                    <div className='flex items-center shrink-0'>
                      <IconButton IconComponent={X} size='xs' variant='ghost' onClick={() => updateFilter({ ...filter, children: filter.children.filter(child => child.id !== childFilter.id) })} />
                    </div>
                  ) : null}
                </div>
              ))}

              {!readOnly && availableAdditionalFields.length > 0 && (
                <div className='flex items-start gap-3'>
                  <div className='min-w-[3rem] shrink-0' />
                  <div className='flex-1 flex justify-start min-w-0'>
                    <GenericSelectionModal
                      options={availableAdditionalFields.map(field => ({ key: field.id, value: field.name }))}
                      onSelect={option => {
                        const foundField = availableAdditionalFields.find(field => field.id === option.key)
                        if (!foundField) {
                          return
                        }
                        const newFilter: DynamicSegmentFilterType = { id: generateUUID(), field: foundField, operator: foundField.operators[0], values: [], children: [] }
                        updateFilter({ ...filter, children: [...filter.children, newFilter] })
                      }}
                      placeholderComponent={
                        <Button variant='ghost' size='sm' leftIcon={PlusCircle} className='h-7 w-fit text-text-sm-medium'>
                          {t('segment_creation.add_properties')}
                        </Button>
                      }
                      showSearch={false}
                      triggerClassName='!border-0 !outline-none !w-fit'
                      contentClassName='w-[12rem]'
                    />
                  </div>
                  <div className='w-8 shrink-0' />
                </div>
              )}
              </div>
            </Fragment>
          )
        }

        return (
          <Fragment key={filter.id}>
            {index > 0 ? (
              <div className={BETWEEN_TOP_LEVEL_WHERE_DIVIDER_CLASS} aria-hidden />
            ) : null}
            <div className={`flex items-start gap-3 pt-2.5 pb-2.5 ${rowPadClass}`}>
            {whereLabel}

            <div className='flex-1 min-w-0 flex flex-col gap-1.5 overflow-visible'>
              {getDynamicSegmentUIFilterType(filter.field) === 'SINGLE' && (
                <>
                  <div className='flex items-start gap-1 flex-wrap'>
                    <div className={FIELD_PILL_CLASS}>
                      <Icon IconComponent={getDynamicSegmentIcon(filter.field.category)} size='xs' className='text-gray-400' />
                      <span className='text-text-sm-medium text-coal-dark'>{filter.field.name}</span>
                    </div>
                  </div>
                  <FilterValueBuilder filter={filter} setFilter={updateFilter} readOnly={readOnly} />
                </>)}
            </div>

            {!readOnly ? (
              <div className='flex items-center shrink-0'>
                <IconButton IconComponent={DotsThreeVertical} size='xs' variant='ghost' />
                <IconButton IconComponent={X} size='xs' variant='ghost' onClick={() => deleteFilter(filter.id)} />
              </div>
            ) : null}
            </div>
          </Fragment>
        )
      })}

      {whereFilters.length > 0 && preparedWhoBlocks.length > 0 ? (
        <div className={WHERE_WHO_DIVIDER_CLASS} aria-hidden />
      ) : null}

      {preparedWhoBlocks.map(({ filter, nameFilter, timeStampFilter }, index) => {
        const remainingFilters = filter.children.filter((childFilter: DynamicSegmentFilterType) => childFilter.id !== nameFilter.id && childFilter.id !== timeStampFilter.id)
        const additionalFieldsList = nameFilter.field.metadata?.additional_fields?.[nameFilter.values[0]] || []
        const availableAdditionalFields: DynamicSegmentFieldType[] = schemaData.fields.filter((field: DynamicSegmentFieldType) => additionalFieldsList.includes(field.id))
        return (
          <Fragment key={filter.id}>
            {index > 0 ? (
              <div className={BETWEEN_TOP_LEVEL_WHERE_DIVIDER_CLASS} aria-hidden />
            ) : null}
            <div className={`flex flex-col pb-2.5 ${rowPadClass}`}>
            {/* Main Who row */}
            <div className='flex items-start gap-3 pt-2.5 pb-2.5'>
              {/* Prefix (Who) */}
              <div className='min-w-[3rem] shrink-0'>
                <div className={`h-[26px] flex items-center pr-1 ${index === 0 ? 'justify-start' : 'justify-end'}`}>
                  <span className={`text-text-sm-medium ${index === 0 ? 'text-zinc-500' : 'text-zinc-500/70'}`}>{index === 0 ? t('segment_creation.who_label') : t('segment_creation.and')}</span>
                </div>
              </div>

              <div className='flex-1 min-w-0 flex flex-col gap-1.5 overflow-visible'>
                {/* Content */}
                <div className='flex items-start gap-1 flex-wrap'>
                  {readOnly ? (
                    <div className={READONLY_OPERATOR_PILL_CLASS}>{nameFilter.operator.label}</div>
                  ) : (
                    <GenericSelectionModal
                      options={nameFilter.field.operators.map(operator => ({ key: operator.key, value: operator.label }))}
                      selectedKey={nameFilter.operator.key}
                      onSelect={option => {
                        updateFilter({ ...filter, children: filter.children.map(child => child.id === nameFilter.id ? { ...nameFilter, operator: nameFilter.field.operators.find(operator => operator.key === option.key) as DynamicSegmentOperatorInfoType } : child) })
                      }}
                      placeholderComponent={
                        <div className={PILL_INTERACTIVE_CLASS}>
                          {nameFilter.operator.label}
                        </div>
                      }
                      showSearch={false}
                      triggerClassName='!border-0'
                      contentClassName='w-48'
                    />
                  )}
                  <div className={FIELD_PILL_CLASS}>
                    <Icon IconComponent={getDynamicSegmentIcon(nameFilter.field.category)} size='xxs' className='text-gray-400' />
                    <span className='text-text-sm-medium text-coal-dark'>{mapEventData(nameFilter.values[0], nameFilter.field.metadata).name}</span>
                  </div>
                </div>
                <div className='flex flex-row flex-nowrap items-center gap-1 min-w-0'>
                  <FilterValueBuilder filter={filter} setFilter={updateFilter} readOnly={readOnly} />
                  <span className='text-text-sm-medium text-zinc-500 shrink-0'>{t('segment_creation.times_label')}</span>
                </div>
                <div className='flex flex-row flex-nowrap items-center gap-1 min-w-0'>
                  <FilterValueBuilder
                    filter={timeStampFilter}
                    setFilter={(f: DynamicSegmentFilterType) => updateFilter({ ...filter, children: filter.children.map(child => child.id === f.id ? f : child) })}
                    renderUnitLabel={false}
                    readOnly={readOnly}
                  />
                  <span className='text-text-sm-medium text-zinc-500 shrink-0'>{t('segment_creation.days_label')}</span>
                </div>
              </div>

              {!readOnly ? (
                <div className='flex items-center shrink-0'>
                  <IconButton IconComponent={DotsThreeVertical} size='xs' variant='ghost' />
                  <IconButton IconComponent={X} size='xs' variant='ghost' onClick={() => deleteFilter(filter.id)} />
                </div>
              ) : null}
            </div>

            {/* Additional property rows - same layout as main row, "and" aligns with "Who" */}
            {remainingFilters.map((childFilter: DynamicSegmentFilterType) => (
              <div key={childFilter.id} className='flex items-start gap-3'>
                {/* Prefix (and) - aligns with Who */}
                <div className='min-w-[3rem] shrink-0'>
                  <div className='h-[26px] flex items-center justify-end pr-1 gap-1'>
                    <Icon IconComponent={ArrowBendDownRight} size='xs' className='text-zinc-500/40' />
                    <span className='text-text-sm-medium text-zinc-500/70'>{t('segment_creation.and')}</span>
                  </div>
                </div>

                {/* Content - same structure as regular condition: [Condition] then [Operator] [Value] */}
                <div className='flex-1 min-w-0 flex flex-col gap-1.5 overflow-visible'>
                  <div className='flex items-start gap-1 flex-wrap'>
                    <div className={FIELD_PILL_CLASS}>
                      <Icon IconComponent={getDynamicSegmentIcon(childFilter.field.category)} size='xs' className='text-gray-400' />
                      <span className='text-text-sm-medium text-coal-dark'>{childFilter.field.name}</span>
                    </div>
                  </div>
                  <div className='flex items-start gap-1 flex-wrap pb-2'>
                    <FilterValueBuilder
                      filter={childFilter}
                      setFilter={(f: DynamicSegmentFilterType) => updateFilter({ ...filter, children: filter.children.map(child => child.id === f.id ? f : child) })}
                      readOnly={readOnly}
                    />
                  </div>
                </div>

                {!readOnly ? (
                  <div className='flex items-center shrink-0'>
                    <IconButton IconComponent={X} size='xs' variant='ghost' onClick={() => updateFilter({ ...filter, children: filter.children.filter(child => child.id !== childFilter.id) })} />
                  </div>
                ) : null}
              </div>
            ))}

            {/* Add property - indented below UTM conditions */}
            {!readOnly && availableAdditionalFields.length > 0 && (
              <div className='flex items-start gap-3'>
                <div className='min-w-[3rem] shrink-0' />
                <div className='flex-1 flex justify-start'>
                  <GenericSelectionModal
                    options={availableAdditionalFields.map(field => ({ key: field.id, value: field.name }))}
                    onSelect={option => {
                      const foundField = availableAdditionalFields.find(field => field.id === option.key)
                      if (!foundField) {
                        return
                      }
                      const newFilter: DynamicSegmentFilterType = { id: generateUUID(), field: foundField, operator: foundField.operators[0], values: [], children: [] }
                      updateFilter({ ...filter, children: [...filter.children, newFilter] })
                    }}
                    placeholderComponent={
                      <Button variant='ghost' size='sm' leftIcon={PlusCircle} className='h-7 w-fit text-text-sm-medium'>
                        {t('segment_creation.add_properties')}
                      </Button>
                    }
                    showSearch={false}
                    triggerClassName='!border-0 !outline-none !w-fit'
                    contentClassName='w-[12rem]'
                  />
                </div>
                <div className='w-8 shrink-0' />
              </div>
            )}
            </div>
          </Fragment>
        )
      })}

      {!readOnly ? (
        <div className='mt-3 px-8'>
          <FilterModal
            trigger={
              <Button variant='dashed' size='sm' leftIcon={PlusCircle} className='bg-transparent h-7 w-fit text-text-sm-medium'>
                {t('segment_creation.add_condition')}
              </Button>
            }
            onSelectField={(filter: DynamicSegmentFilterType) => {
              addNewFilter(filter)
            }}
          />
        </div>
      ) : null}
    </div>
  )
}

export default SegmentConditionFilterLayout
